import { CorporationType, Country } from '@prisma/client';

export const US_EIN_IDENTIFIER_TYPE_ID = 'GID:US:EIN';

export enum GovernmentIdentifierPurpose {
  FORMATION = 'FORMATION',
  TAXATION = 'TAXATION',
  EMPLOYER_WITHHOLDING = 'EMPLOYER_WITHHOLDING',
}

// ----- These types don't belong in this file but will move them when we will fix the documents model -------
export enum BusinessEntityDocumentCategory {
  FORMATION_DOCUMENT = 'FORMATION_DOCUMENT',
  SHAREHOLDER_DOCUMENT = 'SHAREHOLDER_DOCUMENT',
  TAX_AND_COMPLIANCE_DOCUMENT = 'TAX_AND_COMPLIANCE_DOCUMENT',
  FINANCIAL_RECORDS = 'FINANCIAL_RECORDS',
  OFFICE_AND_ADMINISTRATION_DOCUMENT = 'OFFICE_AND_ADMINISTRATION_DOCUMENT',
  CONTRACTS_AND_AGREEMENTS = 'CONTRACTS_AND_AGREEMENTS',
  MISCELLANEOUS = 'MISCELLANEOUS',
}

// MDN file type specifiers: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept#unique_file_type_specifiers
export enum FileTypeSpecifiers {
  'images/*' = 'image/*',
  'audio/*' = 'audio/*',
  'video/*' = 'video/*',
  '.pdf' = '.pdf',
}

// ------------------------------------------------------------------------------------------------

export type IGovernmentIdentifier = {
  id: string | null; // If the id is not present, then the identifier doesn't exist yet.

  country: Country; // ISO 3166 2 alpha country code
  /** Where it is issued, as the content names it: ADM1_US-DE, or ADM0_US for a national number. */
  jurisdictionId: string | null;

  corporationId: number;
  corporationType: CorporationType | null;

  /** What the catalog calls this identifier. Null where nothing has reconciled the key, and for a custom one. */
  identifierTypeId: string | null;
  /** Null until someone records a number: the list carries the identifiers an entity should hold as well as the ones it does. */
  value: string | null;

  displayName: string;
  shortName: string | null;
  description: string | null;
  /** A realistic value, for the field's placeholder. Null where the catalog gives none. */
  example: string | null;
  purposes: GovernmentIdentifierPurpose[];
  regexString: string | null;
  /** The evidence the holder has uploaded. Empty until they upload any; several may be accepted. */
  supportingDocuments: {
    id: number;
    fileName?: string | null; // File name is extensible through the query params.
    fileUrl?: string | null; // File url is extensible through the query params.
  }[];

  /**
   * The documents that can prove this identifier, named the way the catalog names them and narrowed
   * to the entity's legal form.
   */
  acceptedDocuments: { documentTypeId: string; displayName: string }[];
  /** Which file formats the evidence may arrive in. */
  fileTypeSpecifiers: FileTypeSpecifiers[];

  isFunctionCritical: boolean; // Is this government identifier required for a corporation to function legally?

  /** The catalog's ruling that this is its country's primary tax id, e.g. the EIN for the US. */
  isPrimaryTaxId: boolean;
  /** Whether the incorporation filing itself produces this number, e.g. a state filing number. */
  isIssuedByIncorporation: boolean;

  /** Null on an identifier the entity has not recorded: there is no row yet to have been created. */
  createdAt: number | null; // EPOCH time stamp
  lastUpdatedAt: number | null; // EPOCH time stamp

  isCustom: boolean; // Is this government identifier a custom identifier?

  /** Registrations linked to this gov ID. When >1, the user should choose which registration applies. */
  linkedRegistrations?: {
    registrationIdentifier: string;
    displayName: string;
    purpose: string;
  }[];
};
